(function() {
    'use strict';

    /**
     * Leaflet.ImageOverlay.Arrugator by Iván Sánchez Ortega <ivan@sanchezortega.es> https://ivan.sanchezortega.es
     *
     * Licensed under GPLv3.
     *
     * Includes Iván Sánchez's Glii (under GPLv3, see https://gitlab.com/IvanSanchez/arrugator)
     *
     * Includes Vladimir Agafonkin's TinyQueue (under ISC, see https://github.com/mourner/tinyqueue)
     *
     */

    var constantNames = [
        "STATIC_DRAW",
        "DYNAMIC_DRAW",
        "STREAM_DRAW",
        "BYTE",
        "UNSIGNED_BYTE",
        "SHORT",
        "UNSIGNED_SHORT",
        "INT",
        "UNSIGNED_INT",
        "FLOAT",
        "POINTS",
        "LINES",
        "LINE_LOOP",
        "LINE_STRIP",
        "TRIANGLES",
        "TRIANGLE_STRIP",
        "TRIANGLE_FAN",
        "ALPHA",
        "RGB",
        "RGBA",
        "LUMINANCE",
        "LUMINANCE_ALPHA",
        "LINEAR",
        "LINEAR",
        "LINEAR_MIPMAP_LINEAR",
        "LINEAR_MIPMAP_LINEAR",
        "LINEAR_MIPMAP_LINEAR",
        "LINEAR_MIPMAP_LINEAR",
        "REPEAT",
        "CLAMP_TO_EDGE",
        "MIRRORED_REPEAT",
        "RGBA4",
        "RGB565",
        "RGB5_A1",
        "DEPTH_COMPONENT16",
        "STENCIL_INDEX8",
        "DEPTH_STENCIL",
        "NEVER",
        "ALWAYS",
        "LESS",
        "LEQUAL",
        "GREATER",
        "GEQUAL",
        "EQUAL",
        "NOTEQUAL",
    ];

    const factories = {};

    function registerFactory(name, fact) {
        factories[name] = fact;
    }
    class GLFactory {
        constructor(target, contextAttributes) {
            if (!target || !target.constructor || !target.constructor.name) {
                throw new Error(
                    "Invalid target passed to GLFactory constructor. Expected either a HTMLCanvasElement or a WebGLRenderingContext but got " +
                    typeof target +
                    "," +
                    JSON.stringify(target) +
                    "."
                );
            }
            switch (target.constructor.name) {
                case "HTMLCanvasElement":
                    function get(name) {
                        try {
                            return target.getContext(name, contextAttributes);
                        } catch (e) {
                            return undefined;
                        }
                    }
                    this.gl =
                        get("webgl2") ||
                        get("webgl") ||
                        get("experimental-webgl") ||
                        get("webgl-experimental");
                    break;
                case "WebGLRenderingContext":
                case "WebGL2RenderingContext":
                case "bound WebGLRenderingContext":
                case "bound WebGL2RenderingContext":
                    this.gl = target;
                    break;
                default:
                    throw new Error(
                        "Invalid target passed to GLFactory constructor. Expected either a HTMLCanvasElement or a WebGLRenderingContext but got an instance of " +
                        target.constructor.name +
                        "."
                    );
            }
            const gl = this.gl;
            for (let factName in factories) {
                this[factName] = factories[factName](gl, this);
            }
            for (let i in constantNames) {
                const name = constantNames[i];
                this[name] = gl[name];
            }
            if ("canvas" in gl) {
                gl.canvas.addEventListener(
                    "webglcontextlost",
                    (ev) => {
                        console.warn("glii has lost context", ev);
                        ev.preventDefault();
                    },
                    false
                );
                gl.canvas.addEventListener(
                    "webglcontextrestored",
                    (ev) => {
                        console.warn("glii lost context has been restored", ev);
                    },
                    false
                );
                const resizeObserver = new ResizeObserver(() => {
                    this._drawingBufferSizeChanged = true;
                });
                resizeObserver.observe(gl.canvas);
            }
            this._drawingBufferSize = [gl.drawingBufferWidth, gl.drawingBufferHeight];
            this._drawingBufferSizeChanged = false;
        }
        refreshDrawingBufferSize() {
            if (this._drawingBufferSizeChanged) {
                const canvas = this.gl.canvas;
                const { width, height } = canvas.getClientRects()[0];
                canvas.width = width;
                canvas.height = height;
            }
        }
    }

    var typeMap = new Map([
        [Int8Array, 0x1400],
        [Uint8Array, 0x1401],
        [Uint8ClampedArray, 0x1401],
        [Int16Array, 0x1402],
        [Uint16Array, 0x1403],
        [Int32Array, 0x1404],
        [Uint32Array, 0x1405],
        [Float32Array, 0x1406],
    ]);

    class Texture {
        constructor(gl, opts = {}) {
            this._gl = gl;
            this._tex = gl.createTexture();
            this._unit = undefined;
            this._lastActive = performance.now();
            this.minFilter = opts.minFilter || gl.LINEAR;
            this.magFilter = opts.magFilter || gl.LINEAR;
            this.wrapS = opts.wrapS || gl.CLAMP_TO_EDGE;
            this.wrapT = opts.wrapT || gl.CLAMP_TO_EDGE;
            this.internalFormat = opts.internalFormat || gl.RGBA;
            this.format = opts.format || gl.RGBA;
            this._isLoaded = false;
        }
        static _boundUnits = new WeakMap();
        getUnit() {
            this._lastActive = performance.now();
            const gl = this._gl;
            if (this._unit !== undefined) {
                gl.activeTexture(gl.TEXTURE0 + this._unit);
                return this._unit;
            }
            if (!Texture._boundUnits.has(this._gl)) {
                const maxUnits = this._gl.getParameter(
                    this._gl.MAX_COMBINED_TEXTURE_IMAGE_UNITS
                );
                Texture._boundUnits.set(this._gl, new Array(maxUnits));
            }
            const units = Texture._boundUnits.get(this._gl);
            let oldestUnit = -1;
            let oldestTime = Infinity;
            for (let i = 0, l = units.length; i < l; i++) {
                if (units[i] === undefined) {
                    gl.activeTexture(gl.TEXTURE0 + i);
                    gl.bindTexture(gl.TEXTURE_2D, this._tex);
                    units[i] = this;
                    return (this._unit = i);
                }
                if (units[i]._lastActive < oldestTime) {
                    oldestUnit = i;
                    oldestTime = units[i]._lastActive;
                }
            }
            gl.activeTexture(gl.TEXTURE0 + oldestUnit);
            gl.bindTexture(gl.TEXTURE_2D, this._tex);
            units[oldestUnit]._unit = undefined;
            units[oldestUnit] = this;
            return (this._unit = oldestUnit);
        }
        _resetParameters() {
            const gl = this._gl;
            this.getUnit();
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, this.minFilter);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, this.magFilter);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, this.wrapS);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, this.wrapT);
        }
        texImage2D(img) {
            const gl = this._gl;
            this._isLoaded = true;
            this.getUnit();
            gl.texImage2D(
                gl.TEXTURE_2D,
                0,
                this.internalFormat,
                this.format,
                gl.UNSIGNED_BYTE,
                img
            );
            this._resetParameters();
            gl.generateMipmap(gl.TEXTURE_2D);
            return this;
        }
        texArray(w, h, arr) {
            this.getUnit();
            const gl = this._gl;
            gl.texImage2D(
                gl.TEXTURE_2D,
                0,
                this.internalFormat,
                w,
                h,
                0,
                this.format,
                typeMap.get(arr.constructor),
                arr
            );
            this._resetParameters();
            gl.generateMipmap(gl.TEXTURE_2D);
            return this;
        }
        isLoaded() {
            return this._isLoaded;
        }
        getComponentsPerTexel() {
            const gl = this._gl;
            switch (this.format) {
                case gl.RGBA:
                    return 4;
                case gl.RGB:
                    return 3;
                case gl.LUMINANCE_ALPHA:
                    return 2;
                case gl.LUMINANCE:
                case gl.ALPHA:
                    return 1;
                default:
                    throw new Error("Unknown texel data format");
            }
        }
    }
    registerFactory("Texture", function(gl) {
        return class WrappedTexture extends Texture {
            constructor(opts) {
                super(gl, opts);
            }
        };
    });

    class AbstractAttributeSet {
        constructor(gl, options = {}, recordSize = 0) {
            this._gl = gl;
            this._recordSize = recordSize;
            this._size = options.size || 255;
            this._growFactor = options.growFactor;
            this._usage = options.usage || gl.STATIC_DRAW;
            this._buf = gl.createBuffer();
            gl.bindBuffer(gl.ARRAY_BUFFER, this._buf);
            gl.bufferData(gl.ARRAY_BUFFER, this._recordSize * this._size, this._usage);
            if (this._growFactor) {
                this._byteData = new Uint8Array(this._recordSize * this._size);
            }
        }
        static GLSL_TYPE_COMPONENTS = {
            float: 1,
            vec2: 2,
            vec3: 3,
            vec4: 4,
        };
        setBytes(index, offset, data) {
            const upperIndex =
                index + Math.floor((data.byteLength + offset) / this._recordSize);
            if (upperIndex > this._size) {
                this._grow(upperIndex);
            }
            const gl = this._gl;
            if (gl.getParameter(gl.ARRAY_BUFFER_BINDING) !== this._buf) {
                gl.bindBuffer(gl.ARRAY_BUFFER, this._buf);
            }
            const addr = this._recordSize * index + offset;
            gl.bufferSubData(gl.ARRAY_BUFFER, addr, data);
            if (this._byteData) {
                if (data instanceof ArrayBuffer) {
                    this._byteData.set(new Uint8Array(data), addr);
                } else {
                    this._byteData.set(new Uint8Array(data.buffer), addr);
                }
            }
            return this;
        }
        _grow(minimum) {
            if (!this._growFactor) {
                throw new Error(
                    `Non-growable attribute buffer can only hold ${
						this._size
					} records, but tried to set ${minimum + 1}-th record.`
                );
            }
            this._size = Math.max(minimum + 1, Math.ceil(this._size * this._growFactor));
            const newByteData = new Uint8Array(this._recordSize * this._size);
            newByteData.set(this._byteData, 0);
            this._byteData = newByteData;
            const gl = this._gl;
            gl.bindBuffer(gl.ARRAY_BUFFER, this._buf);
            gl.bufferData(gl.ARRAY_BUFFER, this._byteData, this._usage);
        }
    }

    class SingleAttribute extends AbstractAttributeSet {
        constructor(gl, options) {
            const type = options.type || Float32Array;
            const bytesPerElement = type.BYTES_PER_ELEMENT;
            const glslType = options.glslType || "float";
            if (!(glslType in AbstractAttributeSet.GLSL_TYPE_COMPONENTS)) {
                throw new Error(
                    "Invalid value for the `glslType` option; must be `float`, `vec2`, `vec3`, or `vec4`."
                );
            }
            const componentCount = AbstractAttributeSet.GLSL_TYPE_COMPONENTS[glslType];
            super(gl, options, bytesPerElement * componentCount);
            this._glslType = glslType;
            this._componentCount = componentCount;
            this._glType = typeMap.get(type);
            this._normalized = options.normalized;
            if (options.glslType === "float") {
                this.set = this.setNumber;
            } else {
                this.set = this.setArray;
            }
            this._recordBuf = new type(componentCount);
        }
        setNumber() {
            this._recordBuf[0] = value;
            super.setBytes(index, 0, this._recordBuf);
        }
        setArray(index, values) {
            if (values.length !== this._componentCount) {
                throw new Error(
                    `Expected ${this._componentCount} values but got ${values.length}.`
                );
            }
            this._recordBuf.set(values);
            super.setBytes(index, 0, this._recordBuf);
        }
        bindWebGL1(location) {
            const gl = this._gl;
            gl.bindBuffer(gl.ARRAY_BUFFER, this._buf);
            gl.enableVertexAttribArray(location);
            gl.vertexAttribPointer(
                location,
                this._componentCount,
                this._glType,
                this._normalized,
                this._recordSize,
                0
            );
        }
        getGlslType() {
            return this._glslType;
        }
    }
    registerFactory("SingleAttribute", function(gl) {
        return class WrappedSingleAttribute extends SingleAttribute {
            constructor(options) {
                super(gl, options);
            }
        };
    });

    class SequentialIndices {
        constructor(gl, options = {}) {
            this._gl = gl;
            this._size = options.size || 3;
            this._drawMode = options.drawMode === undefined ? gl.TRIANGLES : options.drawMode;
        }
        drawMe() {
            this._gl.drawArrays(this._drawMode, 0, this._size);
        }
    }
    registerFactory("SequentialIndices", function(gl) {
        return class WrappedSequentialIndices extends SequentialIndices {
            constructor(options) {
                super(gl, options);
            }
        };
    });

    class IndexBuffer extends SequentialIndices {
        constructor(gl, options = {}) {
            super(gl, options);
            this._size = options.size || 255;
            this._growFactor = options.growFactor || 1.2;
            this._usage = options.usage || gl.STATIC_DRAW;
            this._type = options.type || gl.UNSIGNED_SHORT;
            if (this._type === gl.UNSIGNED_BYTE) {
                this._bytesPerSlot = 1;
                this._typedArray = Uint8Array;
            } else if (this._type === gl.UNSIGNED_SHORT) {
                this._bytesPerSlot = 2;
                this._typedArray = Uint16Array;
            } else if (this._type === gl.UNSIGNED_INT) {
                this._bytesPerSlot = 4;
                this._typedArray = Uint32Array;
            } else {
                throw new Error(
                    "Invalid type for IndexBuffer. Must be one of `gl.UNSIGNED_BYTE`, `gl.UNSIGNED_SHORT` or `gl.UNSIGNED_INT`."
                );
            }
            this._buf = gl.createBuffer();
            gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this._buf);
            gl.bufferData(
                gl.ELEMENT_ARRAY_BUFFER,
                this._size * this._bytesPerSlot,
                this._usage
            );
            this._activeIndices = 0;
            if (this._growFactor) {
                this._ramData = new this._typedArray(this._size);
            }
        }
        set(n, indices) {
            const gl = this._gl;
            this.grow(n + indices.length);
            gl.bufferSubData(
                gl.ELEMENT_ARRAY_BUFFER,
                n * this._bytesPerSlot,
                this._typedArray.from(indices)
            );
            this._setActiveIndices(n + indices.length);
            if (this._ramData) {
                this._ramData.set(indices, n);
            }
            return this;
        }
        grow(minimum) {
            this.bindMe();
            if (this._size >= minimum) {
                return;
            }
            if (!this._growFactor) {
                throw new Error(
                    `Tried to set index out of bounds of non-growable IndexBuffer (requested ${minimum} vs size ${this._size})`
                );
            } else {
                this._size = Math.max(minimum + 1, Math.ceil(this._size * this._growFactor));
                const newRamData = new this._typedArray(this._size);
                newRamData.set(this._ramData, 0);
                this._ramData = newRamData;
                const gl = this._gl;
                gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, this._ramData, this._usage);
            }
        }
        bindMe() {
            const gl = this._gl;
            if (gl.getParameter(gl.ELEMENT_ARRAY_BUFFER_BINDING) !== this._buf) {
                gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this._buf);
            }
        }
        drawMe() {
            this.bindMe();
            this._gl.drawElements(this._drawMode, this._activeIndices, this._type, 0);
        }
        _setActiveIndices(n) {
            this._activeIndices = Math.max(this._activeIndices, n);
        }
    }
    registerFactory("IndexBuffer", function(gl) {
        return class WrappedIndexBuffer extends IndexBuffer {
            constructor(options) {
                super(gl, options);
            }
        };
    });

    const errRegexp = /ERROR: 0:([0-9]+):(.*)\n/;

    function prettifyGlslError(log, header, src, type, lineOffset) {
        const match = errRegexp.exec(log);
        if (match) {
            let lineNumber = match[1];
            const msg = match[2];
            if (lineNumber < lineOffset) {
                const line = header.split("\n")[lineNumber - 1];
                throw new Error(
                    `Could not compile ${type} shader, reason:\n${msg}\nError lies in injected GLSL headers (check inputs like attributes and uniforms)\nAround line ${lineNumber}: ${line}`
                );
            } else {
                lineNumber -= lineOffset;
                const line = src.split("\n")[lineNumber - 1];
                throw new Error(
                    `Could not compile ${type} shader, reason:\n${msg}\nAround line ${lineNumber}: ${line}`
                );
            }
        }
        throw new Error(`Could not compile ${type} shader, reason in unknown line:\n${log}`);
    }

    class WebGL1Program {
        constructor(
            gl,
            gliiFactory, {
                vertexShaderSource,
                varyings = {},
                fragmentShaderSource,
                indexBuffer,
                attributes = {},
                uniforms = {},
                textures = {},
                target = null,
                depth = 0x0207,
            }
        ) {
            this._gl = gl;
            this._gliiFactory = gliiFactory;
            let attribDefs = "";
            for (let attribName in attributes) {
                const type = attributes[attribName].getGlslType();
                attribDefs += `attribute ${type} ${attribName};\n`;
            }
            let uniformDefs = "";
            this._unifSetters = {};
            for (let uName in uniforms) {
                const uniformType = uniforms[uName];
                switch (uniformType) {
                    case "float":
                        this._unifSetters[uName] = gl.uniform1f.bind(gl);
                        break;
                    case "vec2":
                        this._unifSetters[uName] = gl.uniform2fv.bind(gl);
                        break;
                    case "vec3":
                        this._unifSetters[uName] = gl.uniform3fv.bind(gl);
                        break;
                    case "vec4":
                        this._unifSetters[uName] = gl.uniform4fv.bind(gl);
                        break;
                    case "int":
                        this._unifSetters[uName] = gl.uniform1i.bind(gl);
                        break;
                    case "ivec2":
                        this._unifSetters[uName] = gl.uniform2iv.bind(gl);
                        break;
                    case "ivec3":
                        this._unifSetters[uName] = gl.uniform3iv.bind(gl);
                        break;
                    case "ivec4":
                        this._unifSetters[uName] = gl.uniform4iv.bind(gl);
                        break;
                    case "mat2":
                        this._unifSetters[uName] = (p, v) => gl.uniformMatrix2fv(p, false, v);
                        break;
                    case "mat3":
                        this._unifSetters[uName] = (p, v) => gl.uniformMatrix3fv(p, false, v);
                        break;
                    case "mat4":
                        this._unifSetters[uName] = (p, v) => gl.uniformMatrix4fv(p, false, v);
                        break;
                    default:
                        throw new Error(`Unknown uniform GLSL type "${uniformType}"`);
                }
                uniformDefs += `uniform ${uniformType} ${uName};\n`;
            }
            for (let texName in textures) {
                uniformDefs += "uniform sampler2D " + texName + ";\n";
            }
            let varyingDefs = Object.entries(varyings)
                .map(([n, t]) => `varying ${t} ${n};\n`)
                .join("");
            const precisionHeader = "precision highp float;\n";
            const program = (this._program = gl.createProgram());
            this._compileShader(
                gl.VERTEX_SHADER,
                precisionHeader + attribDefs + varyingDefs + uniformDefs,
                vertexShaderSource
            );
            this._compileShader(
                gl.FRAGMENT_SHADER,
                precisionHeader + varyingDefs + uniformDefs,
                fragmentShaderSource
            );
            gl.linkProgram(program);
            var success = gl.getProgramParameter(program, gl.LINK_STATUS);
            if (!success) {
                console.warn(gl.getProgramInfoLog(program));
                gl.deleteProgram(program);
                throw new Error("Could not compile shaders into a WebGL1 program");
            }
            if (!(indexBuffer instanceof SequentialIndices)) {
                throw new Error(
                    "The WebGL1Program constructor needs a valid `IndexBuffer` to be passed as an option."
                );
            }
            this._indexBuff = indexBuffer;
            this._attrs = attributes;
            this._attribsMap = {};
            for (let attribName in attributes) {
                const loc = gl.getAttribLocation(this._program, attribName);
                this._attribsMap[attribName] = loc;
            }
            this._unifsMap = {};
            this._texs = textures;
            this._unifs = uniforms;
            for (let unifName in uniforms) {
                const loc = gl.getUniformLocation(this._program, unifName);
                if (loc === -1) {
                    console.warn(`Uniform "${unifName}" is not being used in the shaders.`);
                }
                this._unifsMap[unifName] = loc;
            }
            for (let texName in textures) {
                if (texName in this._unifsMap) {
                    throw new Error(
                        `Texture name "${texName}" conflicts with already defined (non-texture) uniform.`
                    );
                }
                const loc = gl.getUniformLocation(this._program, texName);
                if (loc === -1) {
                    console.warn(`Texture "${texName}" is not being used in the shaders.`);
                }
                this._unifsMap[texName] = loc;
            }
            this._target = target;
            this.depth = depth;
        }
        _compileShader(type, header, src) {
            const gl = this._gl;
            const shader = gl.createShader(type);
            gl.shaderSource(shader, "#line 1\n" + header + "#line 10001\n" + src);
            gl.compileShader(shader);
            const success = gl.getShaderParameter(shader, gl.COMPILE_STATUS);
            if (success) {
                return gl.attachShader(this._program, shader);
            }
            const log = gl.getShaderInfoLog(shader);
            gl.deleteShader(shader);
            const readableType = type === gl.VERTEX_SHADER ? "vertex" : "fragment";
            prettifyGlslError(log, header, src, readableType, 10000);
            throw new Error("Could not compile shader.");
        }
        run() {
            const gl = this._gl;
            if (!this._target) {
                gl.bindFramebuffer(gl.FRAMEBUFFER, null);
                this._gliiFactory.refreshDrawingBufferSize();
                gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
            } else {
                gl.bindFramebuffer(gl.FRAMEBUFFER, this._target._fb);
                gl.viewport(0, 0, this._target.width, this._target.height);
            }
            gl.useProgram(this._program);
            if (this.depth === gl.ALWAYS) {
                gl.disable(gl.DEPTH_TEST);
            } else {
                gl.enable(gl.DEPTH_TEST);
                gl.depthFunc(this.depth);
            }
            for (let attribName in this._attrs) {
                this._attrs[attribName].bindWebGL1(this._attribsMap[attribName]);
            }
            for (let texName in this._texs) {
                gl.uniform1i(this._unifsMap[texName], this._texs[texName].getUnit());
            }
            this._indexBuff.drawMe();
            return this;
        }
        setUniform(name, value) {
            this._gl.useProgram(this._program);
            if (name in this._unifSetters) {
                this._unifSetters[name](this._unifsMap[name], value);
                return this;
            } else {
                throw new Error(`Uniform name ${name} is unknown in this WebGL1Program.`);
            }
        }
        getUniformPosition(name) {
            return this._gl.getUniformLocation(this._program, name);
        }
    }
    registerFactory("WebGL1Program", function(gl, gliiFactory) {
        return class WrappedWebGL1Program extends WebGL1Program {
            constructor(opts) {
                super(gl, gliiFactory, opts);
            }
        };
    });

    class TinyQueue {
        constructor(data = [], compare = defaultCompare) {
            this.data = data;
            this.length = this.data.length;
            this.compare = compare;
            if (this.length > 0) {
                for (let i = (this.length >> 1) - 1; i >= 0; i--) this._down(i);
            }
        }
        push(item) {
            this.data.push(item);
            this.length++;
            this._up(this.length - 1);
        }
        pop() {
            if (this.length === 0) return undefined;
            const top = this.data[0];
            const bottom = this.data.pop();
            this.length--;
            if (this.length > 0) {
                this.data[0] = bottom;
                this._down(0);
            }
            return top;
        }
        peek() {
            return this.data[0];
        }
        _up(pos) {
            const { data, compare } = this;
            const item = data[pos];
            while (pos > 0) {
                const parent = (pos - 1) >> 1;
                const current = data[parent];
                if (compare(item, current) >= 0) break;
                data[pos] = current;
                pos = parent;
            }
            data[pos] = item;
        }
        _down(pos) {
            const { data, compare } = this;
            const halfLength = this.length >> 1;
            const item = data[pos];
            while (pos < halfLength) {
                let left = (pos << 1) + 1;
                let best = data[left];
                const right = left + 1;
                if (right < this.length && compare(data[right], best) < 0) {
                    left = right;
                    best = data[right];
                }
                if (compare(best, item) >= 0) break;
                data[pos] = best;
                pos = left;
            }
            data[pos] = item;
        }
    }

    function defaultCompare(a, b) {
        return a < b ? -1 : a > b ? 1 : 0;
    }

    class Arrugator {
        constructor(projector, verts, uv, trigs) {
            this._projector = projector;
            this._verts = verts;
            this._uv = uv;
            this._projVerts = verts.map(projector);
            this._trigs = trigs;
            this._segs = [];
            this._segCount = 0;
            this._segTrigs = [];
            this._epsilons = [];
            this._queue = new TinyQueue([], function(a, b) { return b.epsilon - a.epsilon; });
            this._vertToSeg = new Array(verts.length);
            for (let i in this._verts) {
                this._vertToSeg[i] = [];
            }
            for (let t in this._trigs) {
                let trig = this._trigs[t];
                let v0 = trig[0];
                let v1 = trig[1];
                let v2 = trig[2];
                this._segment(v0, v1, t);
                this._segment(v1, v2, t);
                this._segment(v2, v0, t);
            }
        }
        _segment(v1, v2, t) {
            if (this._vertToSeg[v1] && this._vertToSeg[v1][v2] !== undefined) {
                const found = this._vertToSeg[v1][v2];
                if (!this._segTrigs[found].includes(t)) {
                    this._segTrigs[found].push(t);
                }
                return found;
            }
            const segIdx = this._segCount++;
            this._segs[segIdx] = [v1, v2];
            this._vertToSeg[v1][v2] = segIdx;
            this._vertToSeg[v2][v1] = segIdx;
            this._segTrigs[segIdx] = [t];
            const midpoint = [
                (this._verts[v1][0] + this._verts[v2][0]) / 2,
                (this._verts[v1][1] + this._verts[v2][1]) / 2,
            ];
            const projectedMid = this._projector(midpoint);
            const midProjected = [
                (this._projVerts[v1][0] + this._projVerts[v2][0]) / 2,
                (this._projVerts[v1][1] + this._projVerts[v2][1]) / 2,
            ];
            const epsilon =
                (projectedMid[0] - midProjected[0]) ** 2 +
                (projectedMid[1] - midProjected[1]) ** 2;
            this._queue.push({
                v1: v1,
                v2: v2,
                epsilon: epsilon,
                midpoint: midpoint,
                projectedMid: projectedMid
            });
            return segIdx;
        }
        output() {
            return {
                unprojected: Array.from(this._verts),
                projected: Array.from(this._projVerts),
                uv: Array.from(this._uv),
                trigs: Array.from(this._trigs),
            }
        }
        lowerEpsilon(targetEpsilon) {
            while (this._queue.peek().epsilon > targetEpsilon) {
                this.step();
            }
        }
        step() {
            const top = this._queue.pop();
            const v1 = top.v1;
            const v2 = top.v2;
            const s = this._vertToSeg[v1] && this._vertToSeg[v1][v2];
            const trigs = this._segTrigs[s];
            if (trigs.length >= 3) {
                throw new Error("Somehow a segment is shared by three triangles");
            }
            delete(this._segTrigs[s]);
            delete(this._segs[s]);
            delete(this._vertToSeg[v1][v2]);
            delete(this._vertToSeg[v2][v1]);
            const vm = this._verts.length;
            this._projVerts[vm] = top.projectedMid;
            this._verts[vm] = top.midpoint;
            this._vertToSeg[vm] = [];
            this._uv[vm] = [
                (this._uv[v1][0] + this._uv[v2][0]) / 2,
                (this._uv[v1][1] + this._uv[v2][1]) / 2
            ];
            for (let t of trigs) {
                this._splitTriangle(v1, v2, vm, t);
            }
        }
        _splitTriangle(v1, v2, vm, t) {
            const tvs = this._trigs[t];
            let v3;
            let winding = false;
            if (tvs[0] === v1 && tvs[1] === v2) {
                v3 = tvs[2];
                winding = true;
            } else if (tvs[1] === v1 && tvs[2] === v2) {
                v3 = tvs[0];
                winding = true;
            } else if (tvs[2] === v1 && tvs[0] === v2) {
                v3 = tvs[1];
                winding = true;
            } else if (tvs[1] === v1 && tvs[0] === v2) {
                v3 = tvs[2];
                winding = false;
            } else if (tvs[2] === v1 && tvs[1] === v2) {
                v3 = tvs[0];
                winding = false;
            } else if (tvs[0] === v1 && tvs[2] === v2) {
                v3 = tvs[1];
                winding = false;
            } else {
                throw new Error("Data structure mishap: could not fetch 3rd vertex used in triangle");
            }
            const t2 = this._trigs.length;
            if (winding) {
                this._trigs[t] = [v1, vm, v3];
                this._trigs[t2] = [vm, v2, v3];
            } else {
                this._trigs[t] = [vm, v1, v3];
                this._trigs[t2] = [v2, vm, v3];
            }
            const s1 = this._vertToSeg[v1] && this._vertToSeg[v1][v2];
            const s2 = this._vertToSeg[v2] && this._vertToSeg[v2][v3];
            const s3 = this._vertToSeg[v3] && this._vertToSeg[v3][v1];

            function filterTrig(i) { return i !== t; }
            if (s1 !== undefined) {
                this._segTrigs[s1] = this._segTrigs[s1].filter(filterTrig);
            }
            if (s2 !== undefined) {
                this._segTrigs[s2] = this._segTrigs[s2].filter(filterTrig);
            }
            if (s3 !== undefined) {
                this._segTrigs[s3] = this._segTrigs[s3].filter(filterTrig);
            }
            this._segment(v1, vm, t);
            this._segment(vm, v3, t);
            this._segment(v3, v1, t);
            this._segment(v2, vm, t2);
            this._segment(vm, v3, t2);
            this._segment(v3, v2, t2);
        }
    }

    // import { GLFactory as Glii } from "glii";

    /**
     * @class L.ImageOverlay.Arrugator
     *
     * Displays reprojected raster images.
     *
     * Leverages Glii for not going insane with the WebGL bits, and Arrugator for
     * calculating the triangle mesh for raster reprojection.
     *
     */

    L.ImageOverlay.Arrugator = L.Layer.extend({
        options: {
            // @option padding: Number = 0.1
            // How much to extend the clip area around the map view (relative to its size)
            // e.g. 0.1 would be 10% of map view in each direction
            padding: 0.1,

            // @option epsilon: Number = 1000000
            // Target epsilon for the Arrugator triangle subdivision. Should be equal to the
            // *square* of the maximum error, in projected map units. The default equals
            // 1000 map units.
            epsilon: 1000000,

            // @option fragmentShader: String = "void main() { gl_FragColor = texture2D(uRaster, vUV); }"
            // A `String` containing the WebGL fragment shader to be used. The default
            // just samples the predefined `uRaster` texture with the predefined `vUV`
            // `vec2` varying.
            //
            // Do not change the default unless you know what a fragment shader is.
            fragmentShader: "void main() { gl_FragColor = texture2D(uRaster, vUV); }",
        },

        initialize: function initialize(url, opts) {
            this._src = url;

            // @option controlPoints: Array of Array of Number
            // An array of four pairs of coordinates,
            this._controlPoints = opts.controlPoints;
            if (!(this._controlPoints instanceof Array) || this._controlPoints.length !== 4) {
                throw new Error(
                    "A L.ImageOverlay.Arrugator needs a 'controlPoints' option, which must be an array of four coordinates"
                );
            }

            // @option projector: Function
            // A `Function` that should take an `Array` of two numbers (coordinates in the
            // raster's original projection) and return an `Array` of two numbers
            // (coordinates in the Leaflet display projection).
            //
            // Typically this should be a proj4 forward projection function, like
            // `proj4(srcCRS, displayCRS).forward`
            //
            // It's up to the developer to ensure that the projector function is the adequate
            // for the CRSs.
            this._projector = opts.projector;
            if (!(this._projector instanceof Function)) {
                throw new Error(
                    "A L.ImageOverlay.Arrugator needs a 'projector' option, which must be function for projecting coordinate pairs"
                );
            }

            L.Util.setOptions(this, opts);
        },

        _onFrame: function(e) {
            if (!this.etime) {
                this.etime = 0;
            }

            this.etime++;
            let animFrameNumber = performance.now() - this.options.startTimestamp;
            if (animFrameNumber > this.options.defineUniforms.timeSpan.value) {
                animFrameNumber = this.options.defineUniforms.timeSpan.value;
            }


            this._animFrameNumber = animFrameNumber;
            this._program.setUniform("uNow", animFrameNumber);
            this._program.setUniform("timeSpan", this.options.defineUniforms.timeSpan.value);
            this._program.setUniform("currSrcIndex", this.options.defineUniforms.currSrcIndex.value);



            this._program.run();
            this.etime = 0;

            L.Util.cancelAnimFrame(this._animFrame);
            this._animFrame = L.Util.requestAnimFrame(this._onFrame, this, true);
        },

        onAdd: function onAdd() {
            if (!this._container) {
                this._initContainer();

                if (this._zoomAnimated) {
                    L.DomUtil.addClass(this._container, "leaflet-zoom-animated");
                }
            }

            let _uNowPosition = this._program.getUniformPosition("uNow");
            if (_uNowPosition) {
                L.Util.cancelAnimFrame(this._animFrame);
                this._animFrame = L.Util.requestAnimFrame(this._onFrame, this);
            }

            if (typeof this.options.onFrame == "function") {
                L.Util.cancelAnimFrame(this._animFrame1);
                this._animFrame1 = L.Util.requestAnimFrame(this.options.onFrame, this);
            }

            this.getPane().appendChild(this._container);
            this._reset();
        },

        onRemove: function onRemove() {

            L.Util.cancelAnimFrame(this._animFrame);
            L.Util.cancelAnimFrame(this._animFrame1);
            this._destroyContainer();
        },

        getEvents: L.Renderer.prototype.getEvents,
        // 	_onAnimZoom: L.Renderer.prototype._onAnimZoom,
        _onAnimZoom: function(ev) {
            this._updateTransform(ev.center, ev.zoom);
            this._redraw();
        },
        // 	_onZoom: L.Renderer.prototype._onZoom,
        _onZoom: function(ev) {
            this._update();
            this._redraw();
        },
        _onZoomEnd: L.Util.falseFn,
        _updateTransform: L.Renderer.prototype._updateTransform,

        _initContainer: function _initContainer() {
            this._container = document.createElement("canvas");

            const glii = (this._glii = new GLFactory(this._container, {
                preMultipliedAlpha: false,
            }));

            let textures = {};
            let that = this;
            this._src.forEach(function(item, index) {
                const rasterTexture = new glii.Texture({
                    minFilter: glii.LINEAR,
                    maxFilter: glii.LINEAR,
                });

                const rasterImage = new Image();


                rasterImage.onload = () => {
                    rasterTexture.texImage2D(rasterImage);
                    that.fire("load");

                    if (index == that._src.length - 1) {
                        that._redraw();
                    }
                };

                rasterImage.src = item;

                textures['uRaster' + index] = rasterTexture;
            });

            let sourceUV = [
                [0, 0],
                [0, 1],
                [1, 0],
                [1, 1],
            ];

            this._arrugator = new Arrugator(this._projector, this._controlPoints, sourceUV, [
                [0, 1, 3],
                [0, 3, 2],
            ]);

            this._arrugator.lowerEpsilon(this.options.epsilon);

            let arrugado = this._arrugator.output();

            const pos = new glii.SingleAttribute({
                glslType: "vec2",
                size: arrugado.projected.length,
                growFactor: false,
            });
            const uv = new glii.SingleAttribute({
                glslType: "vec2",
                size: arrugado.uv.length,
                growFactor: false,
            });
            const trigs = (this._trigs = new glii.IndexBuffer({
                size: arrugado.trigs.length * 3,
                growFactor: false,
            }));

            pos.setBytes(0, 0, Float32Array.from(arrugado.projected.flat()));
            uv.setBytes(0, 0, Float32Array.from(arrugado.uv.flat()));
            trigs.set(0, arrugado.trigs.flat());

            const colorTexture = new glii.Texture({
                minFilter: glii.LINEAR,
                maxFilter: glii.LINEAR
            });
            colorTexture.texArray(255, 1, this.options.colorMap);
            //var gl = glii.gl;
            //var textTex = gl.createTexture();
            //gl.activeTexture(gl.TEXTURE0+1);
            //gl.bindTexture(gl.TEXTURE_2D, textTex);

            //gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
            //gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
            //gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
            //gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

            //gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 256,1,0,gl.RGBA,gl.UNSIGNED_BYTE, this.options.colorMap);
            //gl.uniform1i(gl.getUniformLocation(this._program._program, "colorMap"),1);

            textures.colorMap = colorTexture;
            var uniforms = {
                uCenter: "vec2",
                uScale: "vec2",
                uNow: "float"
            };

            if (this.options.defineUniforms) {
                for (let uniformKey in this.options.defineUniforms) {
                    uniforms[uniformKey] = this.options.defineUniforms[uniformKey].type;
                }
            }

            this._program = new glii.WebGL1Program({
                vertexShaderSource: `
				void main() {
					gl_Position = vec4(
						(aPos.xy - uCenter) / vec2(uScale),
						1.0,
						1.0
					);
					vUV = aUV;
				}`,
                varyings: { vUV: "vec2" },
                fragmentShaderSource: this.options.fragmentShader,
                indexBuffer: this._trigs,
                attributes: {
                    aPos: pos,
                    aUV: uv,
                },
                textures: textures,
                uniforms: uniforms,
            });
        },

        _destroyContainer: function _destroyContainer() {
            delete this._glii;
            L.DomUtil.remove(this._container);
            L.DomEvent.off(this._container);
            delete this._container;
        },

        _redraw: function _redraw(ev) {
            var crs = this._map.options.crs,
                projcenter = crs.project(this._center),
                mapPxSize = this._bounds.getSize(),
                scale = mapPxSize.divideBy(crs.scale(this._zoom) / 20037508);


            this._program.setUniform("uCenter", [projcenter.x, projcenter.y]);
            this._program.setUniform("uScale", [scale.x, scale.y]);
            this._program.run();
        },

        _update: function _update() {
            if (this._map._animatingZoom && this._bounds) {
                return;
            }
            L.Renderer.prototype._update.call(this);

            this._center = this._map.getCenter();
            this._zoom = this._map.getZoom();

            L.Browser.retina ? 2 : 1;
            let mapPxSize = this._bounds.getSize();
            // 		    mapPxSize = this._map.getSize();

            this._container.width = /*m * */ mapPxSize.x;
            this._container.height = /*m * */ mapPxSize.y;
            L.DomUtil.setPosition(this._container, this._bounds.min);

            this._redraw();
        },

        _reset: function() {
            this._update();
            this._updateTransform(this._center, this._zoom);
        },

        _setTexture: function(texName, src) {
            let glii = this._glii;

            const rasterTexture = new glii.Texture({
                minFilter: glii.LINEAR,
                maxFilter: glii.LINEAR,
            });

            const rasterImage = new Image();

            var that = this;
            rasterImage.onload = () => {
                rasterTexture.texImage2D(rasterImage);
                that.fire("load");

                if (index == that._src.length - 1) {
                    that._redraw();
                }
            };

            rasterImage.src = src;

            this._program._texs[texName] = rasterTexture;
        },

        _deleteTexture: function(texName) {
            this._glii.gl.deleteTexture(this._program._texs[texName]._tex)
        }
    });

    L.imageOverlay.arrugator = function arrugator(url, opts) {
        return new L.ImageOverlay.Arrugator(url, opts);
    };

}());
//# sourceMappingURL=leaflet.imageoverlay.arrugator.js.map